import { productApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  SimpleProductForFrontI,
  ProductI,
  GetProductParamsI,
} from "@interfaces";
import { AxiosError } from "axios";

// ── helper token ─────────────────────────────────────────
const token = () => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  return t;
};

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error).message);
};

// ── GET paginado ──────────────────────────────────────────
const getProducts = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  categoryId,
  status,
}: GetProductParamsI): Promise<ServiceResponseI<DataI<ProductI>>> => {
  try {
    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId)             params.append("clientId",  clientId.toString());
    if (categoryId)           params.append("categoryId", categoryId.toString());
    if (status !== undefined) params.append("status",    String(status));

    const { data } = await productApi.get<ServiceResponseI<DataI<ProductI>>>(
      "", { params, headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── GET by id ─────────────────────────────────────────────
export const productById = async (
  id: string,
): Promise<ServiceResponseI<ProductI>> => {
  try {
    const { data } = await productApi.get<ServiceResponseI<ProductI>>(
      `/${id}`,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── POST ──────────────────────────────────────────────────
export const productPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<SimpleProductForFrontI>> => {
  try {
    const { data } = await productApi.post<ServiceResponseI<SimpleProductForFrontI>>(
      "", payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── PUT ───────────────────────────────────────────────────
export const productPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await productApi.put<ServiceResponseI<string>>(
      `/${id}`, payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── GET all sin paginación ────────────────────────────────
export const getAllProducts = async (
  categoryId?: string | null,
  branchId?:   string | null,
): Promise<ServiceResponseI<ProductI[]>> => {
  try {
    const { data } = await productApi.get<ServiceResponseI<ProductI[]>>(
      "/all-no-pagination",
      { params: { branchId, categoryId }, headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── DELETE ────────────────────────────────────────────────
export const productDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await productApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── SET STATUS ────────────────────────────────────────────
export const productSetStatus = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await productApi.patch<ServiceResponseI<string>>(
      `/set-status/${id}`,
      {},
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

// ── HOOK ──────────────────────────────────────────────────
export const useProducts = (params: GetProductParamsI) => {
  const productsQuery = useQuery(
    ["products", params],
    () => getProducts(params),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { productsQuery };
};